import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { i18n } from "i18next";
import { Autocomplete, Divider, FormControlLabel, Stack, Switch, TextField } from "@mui/material";
import { DataUsageOutlined } from '@mui/icons-material';
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { QueryField } from "system/query/QueryInfo";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import KeywordOptionSelector from "components/widgetParts/KeywordOptionSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import CategoryArcsPreview from "./CategoryArcsPreview";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetQueryBehavior, WidgetRequest, WidgetTemplateQueryBehavior } from "system/widget/Widget";
import { BuildDefaultCategoryWidgetRequestBodyFunction } from "components/widgets/utility/CategoryChartsAPIResponseHandlers";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import QueryWidgetSettingsMissingQueryInfoAlert from "components/widgetParts/queryWidgetComposition/QueryWidgetSettingsMissingQueryInfoAlert";
import MultiQueryRequestSettings, { MultiQueryRequestSettingsPresets } from "components/widgetParts/MultiQueryRequestSettings";
import { getCategorySortingOptions } from "../ShareOfVoice/ShareOfVoiceSettings";
import { formatNestedLabel } from "system/Utilities";

const defaultWidgetRequest = {
  name: 'category-arcs',
  endpoint: 'query/agg',
  queryParams: {
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL
};

export type CategoryArcsSettingsStateType = {
  arcSize: "s" | "m" | "l";
  columns: number;
  customWidgetTitle: string;
  displayCount: string;
  layout: "f" | "h" | "v";
  maxDisplayed: number;
  sortValue: "none" | "asc" | "desc";
  widgetTitle: string;
  widgetTitleAlignment: string;
}

export const defaultSettings: CategoryArcsSettingsStateType = {
  arcSize: "m",
  columns: 3,
  customWidgetTitle: '',
  displayCount: '',
  layout: "f",
  maxDisplayed: 20,
  sortValue: "none",
  widgetTitle: 'default',
  widgetTitleAlignment: 'left'
}

export function getMaximumDisplayCountOptions(i18n: i18n) {
  return [
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 },
    { label: "4", value: 4 },
    { label: "5", value: 5 },
    { label: "10", value: 10 },
    { label: "15", value: 15 },
    { label: `20 - ${i18n.t('default')}`, value: 20 },
    { label: "25", value: 25 },
    { label: "30", value: 30 },
    { label: "35", value: 35 },
    { label: "40", value: 40 },
    { label: "45", value: 45 },
    { label: "50", value: 50 },
    { label: "60", value: 60 },
    { label: "80", value: 80 },
    { label: "100", value: 100 }
  ];
}

export const BuildDefaultCategoryArcsRequests = BuildDefaultCategoryWidgetRequestBodyFunction(defaultWidgetRequest);

export function getInitialAggState(props: WidgetSettingsProps) {
  const request = getSingleOriginalRequestByName(props.activeWidgetRequests, "category-arcs");
  const requestBody = request?.requestInit?.body || {};
  const multiRequestBody = request?.multiRequestInit?.body || {};

  const multiAggs = multiRequestBody?.aggs || requestBody?.aggs || {};
  const keys = Object.keys(multiAggs || {});
  if (keys.length > 0) {
    const firstKey = keys[0];
    const agg = multiAggs?.[firstKey];
    const aggField = agg?.field;
    const foundField = props.queryInfo?.fields?.find((field: QueryField) => field.id === aggField);
    return {
      selectedField: foundField,
      selectedMultiQueryKeywordOption: agg?.filter?.[0] || undefined,
      selectedMultiQueryKeywordLabel: agg?.label?.[0] || undefined,
      includeNull: !!agg?.include_null,
      excludedCategories: (agg?.exclude as string[] | undefined) || [],
    };
  }
}

export default function CategoryArcsSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const initialAggState = useMemo(() => getInitialAggState(props), [props.widget?.request_info, props.queryInfo]);
  const maximumDisplayCountOptions = getMaximumDisplayCountOptions(i18n);
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'category-arcs');
  const isMultiQuery = Array.isArray(props.activeWidgetRequests[0]) && props.activeWidgetRequests[0].length > 1;

  useEffect(() => {
    const request = getSingleOriginalRequestByName(props.activeWidgetRequests, "category-arcs");
    const multiAggs = request?.multiRequestInit?.body?.aggs || request?.requestInit?.body?.aggs || {};
    const keys = Object.keys(multiAggs || {});

    if (keys.length > 0) {
      const firstKey = keys[0];
      const agg = multiAggs?.[firstKey];
      const aggField = agg?.field;
      const foundField = props.queryInfo?.fields?.find((field: QueryField) => field.id === aggField);
      setSelectedMultiQueryKeywordOption(agg?.filter?.[0] || undefined);
      if (foundField) {
        setSelectedField(foundField);
      }
      setIncludeNull(!!agg?.include_null);
      setExcludedCategories(agg?.exclude || []);
    }
  }, [props.widget?.request_info, props.queryInfo]);

  const [selectedField, setSelectedField] = useState<QueryField | undefined>(initialAggState?.selectedField);
  const [selectedMultiQueryKeywordOption, setSelectedMultiQueryKeywordOption] = useState<string | undefined>(initialAggState?.selectedMultiQueryKeywordOption);
  const [selectedMultiQueryKeywordLabel, setSelectedMultiQueryKeywordLabel] = useState<string | undefined>(initialAggState?.selectedMultiQueryKeywordLabel);
  const [includeNull, setIncludeNull] = useState<boolean | undefined>(initialAggState?.includeNull);
  const [excludedCategories, setExcludedCategories] = useState<string[]>(initialAggState?.excludedCategories || []);

  const [autoWidgetQueryBehavior, setAutoWidgetQueryBehavior] = useState<AutoWidgetQueryBehavior>(originalRequest?.autoWidgetQueryBehavior || AutoWidgetQueryBehavior.CUSTOM);
  const [widgetQueryBehavior, setWidgetQueryBehavior] = useState<WidgetQueryBehavior>(originalRequest?.widgetQueryBehavior || WidgetQueryBehavior.SINGLE_INTERSECTION);
  const [widgetTemplateQueryBehavior, setWidgetTemplateQueryBehavior] = useState<WidgetTemplateQueryBehavior>(originalRequest?.widgetTemplateQueryBehavior || WidgetTemplateQueryBehavior.SINGLE_COMBINED);

  const updateAggs = () => {
    if (!selectedField) {
      return;
    }
    const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };

    let multiQueryKeywordOption = selectedMultiQueryKeywordOption;
    if (!selectedMultiQueryKeywordOption && selectedField?.options) {
      multiQueryKeywordOption = selectedField?.options[0].id || undefined;
    }

    const singleAggs:any = {
      op: "keyword",
      field: selectedField.id,
      aggs: { "percent": { op: "percent" }  }
    };

    const multiAggs:any = {
      op: "keyword",
      field: selectedField.id,
      aggs: { "percent": { op: "percent", of_total: true }  }
    };
    if (multiQueryKeywordOption) {
      multiAggs.filter = [multiQueryKeywordOption];
      multiAggs.label = [selectedMultiQueryKeywordLabel];
    }
    if (includeNull) {
      singleAggs.include_null = true;
      multiAggs.include_null = true;
    }

    if (excludedCategories.length > 0) {
      singleAggs.exclude = excludedCategories;
      multiAggs.exclude = excludedCategories;
    }

    singleAggs.bucket_count = settingsState.maxDisplayed;
    multiAggs.bucket_count = settingsState.maxDisplayed;

    widgetRequest.autoWidgetQueryBehavior = autoWidgetQueryBehavior;
    widgetRequest.widgetQueryBehavior = widgetQueryBehavior;
    widgetRequest.widgetTemplateQueryBehavior = widgetTemplateQueryBehavior;

    if (selectedField.label && selectedField.id) {
      widgetRequest.requestInit = { body: { aggs: { [selectedField.label]: singleAggs } } };
      widgetRequest.multiRequestInit = { body: { aggs: { [selectedField.label]: multiAggs } } };
      props.saveWidgetDataAndRequests({settings: settingsState}, [widgetRequest])
    }
  }

  const arcSizeOptions = useMemo(() => ([
    { icon: <DataUsageOutlined fontSize="small" />, value: 's' },
    { icon: <DataUsageOutlined fontSize="medium" />, value: 'm' },
    { icon: <DataUsageOutlined fontSize="large" />, value: 'l' },
  ]), []);

  const layoutOptions: OptionsValueType[] = useMemo(() => ([
    { label: i18n.t("widget-types.category-arcs.settings.fixed"), value: 'f' },
    { label: i18n.t("widget-types.category-arcs.settings.auto"), value: 'h' },
    { label: i18n.t("widget-types.category-arcs.settings.vertical"), value: 'v' },
  ]), []);

  const sortingOptions = getCategorySortingOptions(i18n);

  const [settingsState, setSettingsState] = useState<CategoryArcsSettingsStateType>({
    ...defaultSettings,
    ...props?.widget?.data?.settings
  });
  const settingsOnChange = (updatedState: CategoryArcsSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.category-arcs.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && <>
          {props.queryInfo && <>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <QueryFieldSelector
              requireAggSupported={true}
              fields={props.queryInfo.fields}
              allowedFieldTypes={["keyword", "keywords"]}
              selectedFieldId={selectedField?.id || ""}
              setSelectedFieldId={(newFieldId:string) => {
                const newSelectedField = props.queryInfo?.fields?.find((field: QueryField) => field.id === newFieldId);
                setSelectedField(newSelectedField);
                const fieldId = newSelectedField?.options?.[0].id ?? undefined;
                setSelectedMultiQueryKeywordOption(fieldId);
                setExcludedCategories([]);
              }} />
            <FormControlLabel
              control={
                <Switch
                  checked={!includeNull}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setIncludeNull(!event.target.checked);
                  }}/>}
              label={i18n.t("requestOptions.only-rows-with-value")} />
            <SimpleFormLabel>{i18n.t("widget-types.word-cloud.settings.excluded-terms")}</SimpleFormLabel>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={(selectedField?.options || []).filter((option) => option.id !== null)}
              getOptionLabel={(option) => formatNestedLabel(option.label || String(option.id))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={excludedCategories.map((id) =>
                selectedField?.options?.find((option) => option.id === id) || { id, label: id }
              )}
              onChange={(event, newValues) => setExcludedCategories(newValues.map((option) => option.id as string))}
              renderInput={(params) => <TextField autoComplete="off" {...params} />} />
            <Divider sx={{ my: 2}}/>
            <SimpleFormLabel>{i18n.t("widget-types.category-arcs.settings.multi-query-field")}</SimpleFormLabel>
            <KeywordOptionSelector
              queryInfo={props.queryInfo}
              selectedField={selectedField}
              selectedKeywordOption={selectedMultiQueryKeywordOption}
              selectedKeywordLabel={selectedMultiQueryKeywordLabel}
              setSelectedKeywordOption={(newKeywordOption: string | undefined, newKeywordOptionLabel?: string | undefined) => {
                setSelectedMultiQueryKeywordOption(newKeywordOption || "");
                setSelectedMultiQueryKeywordLabel(newKeywordOptionLabel || "");
              }}
            />
            <MultiQueryRequestSettings
              presetOptionsType={MultiQueryRequestSettingsPresets.AUTO_MULTI}
              autoWidgetQueryBehavior={autoWidgetQueryBehavior}
              setAutoWidgetQueryBehavior={(behavior: AutoWidgetQueryBehavior) => setAutoWidgetQueryBehavior(behavior)}
              widgetQueryBehavior={widgetQueryBehavior}
              setWidgetQueryBehavior={(behavior: WidgetQueryBehavior) => setWidgetQueryBehavior(behavior)}
              widgetTemplateQueryBehavior={widgetTemplateQueryBehavior}
              setWidgetTemplateQueryBehavior={(behavior: WidgetTemplateQueryBehavior) => setWidgetTemplateQueryBehavior(behavior)} />
          </>}
          {(!props.queryInfo) &&
            <QueryWidgetSettingsMissingQueryInfoAlert />
          }
        </>}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <CategoryArcsPreview settingsState={settingsState} />
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                {/* Widget Title */}
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle} />
                  </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>

              {/* Layout */}
              <SettingsSingleSelect
                defaultValue={layoutOptions.find((item: OptionsValueType) => (
                  item.value === 'fixed'
                )) ?? layoutOptions[0]}
                keyid="layout"
                label={i18n.t("layout")}
                options={layoutOptions}
                value={layoutOptions.find((item: OptionsValueType) => (
                  item.value === settingsState.layout
                ))}
              />

              {/* Maximum Arcs Displayed */}
              <SettingsSingleSelect
                defaultValue={maximumDisplayCountOptions.find(item => item.value === 20) || maximumDisplayCountOptions[0]}
                keyid="maxDisplayed"
                label={i18n.t("widget-types.category-arcs.settings.maxDisplayed")}
                options={maximumDisplayCountOptions}
                value={maximumDisplayCountOptions.find((item: OptionsValueType) => ( item.value === settingsState.maxDisplayed ))}
              />

              {/* Sorting Options */}
              {isMultiQuery && 
                <SettingsSingleSelect
                  defaultValue={sortingOptions[0]}
                  keyid="sortValue"
                  label={i18n.t("widgets.settings.sorting.label")}
                  options={sortingOptions}
                  value={sortingOptions.find((item: OptionsValueType) => item.value === settingsState.sortValue) || sortingOptions[0]}
                />
              }

              {settingsState.layout === 'f' &&
                <>
                  <SettingsSlider
                    defaultValue={3}
                    keyid="columns"
                    label={i18n.t("columns")}
                    max={10}
                    min={1}
                    showCurrentValue={true}
                    sliderValue={settingsState.columns}
                    step={1} />
                  <SettingsToggleButton
                    buttonStyle={{ minWidth: "51px" }}
                    keyid="arcSize"
                    label={i18n.t("widget-types.category-arcs.settings.arc-size")}
                    optionsList={arcSizeOptions}
                    value={settingsState.arcSize} />
                </>}
              </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  );
}
